import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { OrderStatusBadge } from "@/components/ui/status-badge";
import { Skeleton } from "@/components/ui/skeleton";
import { OrderHistory } from "@/components/orders/OrderHistory";
import { OrderNotesCard } from "@/components/orders/OrderNotesCard";
import { OrderEditDialog } from "@/components/orders/OrderEditDialog";
import { BolViewerDialog } from "@/components/orders/BolViewerDialog";
import { TenderViewerDialog } from "@/components/orders/TenderViewerDialog";
import { MultiLegRouteChain } from "@/components/orders/MultiLegRouteChain";
import { OrderDetailsSections } from "@/components/orders/OrderDetailsSections";
import { useOrderOosRealtime } from "@/hooks/useOrderOosRealtime";
import { useConfirmDocGate } from "@/hooks/useConfirmDocGate";
import { confirmBlockActionHint, LOAD_STATUS_MISMATCH, LOAD_STATUS_MISMATCH_MESSAGE } from "@/lib/confirm-doc-gating";
import { useConfirmOrder } from "@/hooks/useConfirmOrder";
import { ConfirmLegsDialog } from "@/components/orders/ConfirmLegsDialog";
import { LegStatusDialog } from "@/components/orders/LegStatusDialog";
import { planLegStatusActions } from "@/lib/leg-status-actions";
import { useLegStatusProgression } from "@/hooks/useLegStatusProgression";
import { useAuth } from "@/hooks/useAuth";
import { useOrderLoadStatusAtDelivery } from "@/hooks/useOrderLoadStatusAtDelivery";
import { resolveOrderEndpoint } from "@/lib/order-endpoints";
import { LEG_STATUS_RANK, resolveLegStatus } from "@/lib/leg-status-resolve";
import { useOrderLegs } from "@/hooks/useOrderLegs";
import { legMirrorsParentEquipment } from "@/lib/confirm-plan-legs";

// eapi-trip-mapper no longer needed for cancellation — using cancelTrip directly


import {
  Order,
  Customer,
  Tractor,
  Driver,
  ORDER_STATUS_LABELS,
  OrderStatus,
} from "@/types/tms";
import { useTrailerTypes, getTrailerTypeLabel } from "@/hooks/useTrailerTypes";
import { format } from "date-fns";
import {
  isLateDeparture,
  isLateArrival,
} from "@/lib/order-time";
import { ReadyTimeDisplay, DeliveryTimeDisplay } from "@/components/orders/OrderTimeDisplay";
import type { Location } from "@/types/tms";
import {
  ArrowRight,
  ArrowUpRight,
  ArrowDownRight,
  Pencil,
  CheckCircle2,
  MoreHorizontal,
  XCircle,
  Clock,
  Truck,
  FileText,
  Activity,
  CheckCircle,
  LifeBuoy,
  PackageCheck,
  Loader2,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { CancelOrderDialog } from "@/components/orders/CancelOrderDialog";
import { AssignRescueDialog } from "@/components/orders/AssignRescueDialog";
import { useOrderCancellation } from "@/hooks/useOrderDeletion";
import { useOrderStatusTransition } from "@/hooks/useOrderStatusTransition";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  mapOrderToCreateTrip,
  mapLegToCreateTrip,
  assertTripBodyHasLatLng,
} from "@/lib/eapi-trip-mapper";
import { correctStaleCreateResponse } from "@/lib/per-leg-trip-sync";
import { isNoEapiState } from "@/lib/eapi-eligibility";
import { buildCustomFieldsForOrder } from "@/lib/eapi-custom-fields";
import {
  listActiveLegTripsForOrder,
  supersedeLegTrip,
  insertActiveLegTrip,
  findLegIdByActiveTripId,
  syncOrderLegsAndTrip,
} from "@/lib/leg-trip-sync";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { StarOrderButton } from "@/components/orders/StarOrderButton";
import { useUserRole } from "@/hooks/useUserRole";
import { useEapiAuth } from "@/hooks/useEapiAuth";
import { useEapiStatusSync } from "@/hooks/useEapiStatusSync";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage,
} from "@/components/ui/breadcrumb";
import { Link } from "react-router-dom";
import { useState, useEffect, useRef, useMemo } from "react";
import { useTenant } from "@/hooks/useTenant";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Repeat } from "lucide-react";
import { CancellationBanner } from "@/components/orders/CancellationBanner";
import { ConfirmDocsOverrideDialog } from "@/components/orders/ConfirmDocsOverrideDialog";
import { MapContainer, Marker, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { ThemedTileLayer } from "@/components/atlas/ThemedTileLayer";
import { TrailerArrivedNotice } from "@/components/orders/TrailerArrivedNotice";
import { useLegsWithArrivedTrailer } from "@/hooks/useLegsWithArrivedTrailer";

type OrderWithRelations = Order & {
  billing_party?: Customer | null;
  assigned_tractor?: Tractor | null;
  assigned_operator?: Driver | null;
};

// Fix Leaflet default icon issue
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

const originIcon = L.divIcon({
  className: "",
  html: `<div style="background:#22c55e;color:white;width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:13px;font-weight:700;border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,.35)">A</div>`,
  iconSize: [28, 28],
  iconAnchor: [14, 14],
});

const destIcon = L.divIcon({
  className: "",
  html: `<div style="background:#ef4444;color:white;width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:13px;font-weight:700;border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,.35)">B</div>`,
  iconSize: [28, 28],
  iconAnchor: [14, 14],
});

function makeNumberedIcon(n: number) {
  return L.divIcon({
    className: "",
    html: `<div style="background:hsl(var(--primary));color:hsl(var(--primary-foreground));width:24px;height:24px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:700;border:2px solid white;box-shadow:0 2px 4px rgba(0,0,0,.3)">${n}</div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
  });
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between items-start py-2">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium text-right max-w-[60%]">{value || "—"}</span>
    </div>
  );
}

// Status progression component
const STATUS_ORDER: OrderStatus[] = ["booked", "confirmed", "tender_received", "in_transit", "delivered"];

function StatusTimeline({ currentStatus }: { currentStatus: OrderStatus }) {
  if (currentStatus === "canceled") {
    return (
      <div className="flex items-center gap-2 py-2">
        <div className="h-3 w-3 rounded-full bg-destructive" />
        <span className="text-sm font-medium text-destructive">Canceled</span>
      </div>
    );
  }

  const currentIdx = STATUS_ORDER.indexOf(currentStatus);

  return (
    <div className="flex items-center gap-1 w-full">
      {STATUS_ORDER.map((status, idx) => {
        const isCompleted = idx <= currentIdx;
        const isCurrent = idx === currentIdx;
        return (
          <div key={status} className="flex items-center flex-1 last:flex-initial">
            <div className="flex flex-col items-center gap-1">
              <div
                className={`h-3 w-3 rounded-full border-2 transition-colors ${
                  isCompleted
                    ? "bg-primary border-primary"
                    : "bg-background border-muted-foreground/30"
                } ${isCurrent ? "ring-2 ring-primary/30 ring-offset-1 ring-offset-background" : ""}`}
              />
              <span
                className={`text-[10px] whitespace-nowrap ${
                  isCompleted ? "text-foreground font-medium" : "text-muted-foreground"
                }`}
              >
                {ORDER_STATUS_LABELS[status]}
              </span>
            </div>
            {idx < STATUS_ORDER.length - 1 && (
              <div
                className={`flex-1 h-0.5 mx-1 mt-[-18px] ${
                  idx < currentIdx ? "bg-primary" : "bg-muted-foreground/20"
                }`}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

// Map bounds fitter
function FitBounds({ coords }: { coords: [number, number][] }) {
  const map = useMap();
  const fitted = useRef(false);

  useEffect(() => {
    if (coords.length >= 2 && !fitted.current) {
      fitted.current = true;
      const bounds = L.latLngBounds(coords.map(([lat, lng]) => [lat, lng]));
      map.fitBounds(bounds, { padding: [40, 40] });
    }
  }, [coords, map]);

  return null;
}

// Geocode helper
async function geocodeAddress(address: string): Promise<[number, number] | null> {
  try {
    const { data } = await supabase.functions.invoke("geocode-address", {
      body: { address },
    });
    if (data?.latitude != null && data?.longitude != null) return [data.latitude, data.longitude];
    return null;
  } catch {
    return null;
  }
}

// Fetch driving route between two coords (returns [lat,lng][] or null)
async function fetchRoute(
  origin: [number, number],
  dest: [number, number],
): Promise<[number, number][] | null> {
  try {
    // origin/dest are [lat, lng], Mapbox wants [lng, lat]
    const { data } = await supabase.functions.invoke("mapbox-directions", {
      body: {
        origin: [origin[1], origin[0]],
        destination: [dest[1], dest[0]],
      },
    });
    return data?.coordinates ?? null;
  } catch {
    return null;
  }
}

function buildAddress(street: string, city: string, state?: string | null, country?: string | null) {
  return [street, city, state, country].filter(Boolean).join(", ");
}

export default function OrderDetail() {
  const { data: trailerTypes } = useTrailerTypes();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const { tenantId, tenantReady, activeTenant, setActiveTenant } = useTenant();
  const { isReadOnly, isAdmin } = useUserRole();

  // Live reaction to tractor OOS unassignments for this order.
  useOrderOosRealtime(id);

  // Optional ?tenant=<slug> deep-link: auto-switch the active tenant so links
  // from external tools land on the correct environment instead of 404'ing.
  useEffect(() => {
    const requested = searchParams.get("tenant");
    if (!requested) return;
    const allowed = ["dev", "staging", "pre-prod", "prod"] as const;
    if (!(allowed as readonly string[]).includes(requested)) {
      console.warn(`[OrderDetail] Ignoring invalid ?tenant=${requested}`);
    } else if (requested !== activeTenant) {
      setActiveTenant(requested as typeof allowed[number]);
    }
    // Strip the param from the URL either way to keep it clean.
    const next = new URLSearchParams(searchParams);
    next.delete("tenant");
    const qs = next.toString();
    navigate(`/orders/${id}${qs ? `?${qs}` : ""}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [editOrder, setEditOrder] = useState<Order | null>(null);
  const { user } = useAuth();
  const [docsOverridden, setDocsOverridden] = useState(false);
  // Multi-stop orders pick which stops to confirm; one-stop orders confirm
  // straight away, so this stays closed for them.
  const [showConfirmLegs, setShowConfirmLegs] = useState(false);
  // Dispatch's per-leg status control (Update Leg Status).
  const [showLegStatus, setShowLegStatus] = useState(false);
  // Admin acknowledgement of confirming without required billing documentation.
  const [showDocsOverride, setShowDocsOverride] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showRescueDialog, setShowRescueDialog] = useState(false);
  const [originCoord, setOriginCoord] = useState<[number, number] | null>(null);
  const [destCoord, setDestCoord] = useState<[number, number] | null>(null);
  const [routeCoords, setRouteCoords] = useState<[number, number][] | null>(null);
  const [multiLegCoords, setMultiLegCoords] = useState<([number, number] | null)[]>([]);
  const [multiLegRoutes, setMultiLegRoutes] = useState<([number, number][] | null)[]>([]);

  const [selectedLegId, setSelectedLegId] = useState<string | null>(null);
  const routeStrokeColor = useMemo(() => {
    if (typeof window === "undefined") return "hsl(222 47% 20%)";

    const primary =
      getComputedStyle(document.body).getPropertyValue("--primary").trim() ||
      getComputedStyle(document.documentElement).getPropertyValue("--primary").trim();

    return primary ? `hsl(${primary})` : "hsl(222 47% 20%)";
  }, []);

  const { data: order, isLoading } = useQuery({
    queryKey: ["order-detail", id, tenantId],
    enabled: !!id && tenantReady && !!tenantId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("orders")
        .select(`
          *,
          billing_party:customers(*),
          assigned_tractor:tractors(*),
          assigned_operator:drivers!assigned_operator_id(*),
          assigned_operator_2:drivers!assigned_operator_2_id(*)
        `)
        .eq("id", id!)
        .eq("tenant_id", tenantId)
        .maybeSingle();
      if (error) throw error;
      if (!data) {
        throw new Error(
          `Order ${id} isn't visible in this workspace — it may belong to another environment or you may not have access to it.`,
        );
      }
      return data as OrderWithRelations;
    },
  });

  // Document Confirm Gating (Prod tenant only): required billing docs that
  // are still missing for this order's billing party. Inert elsewhere.
  const confirmDocGate = useConfirmDocGate({
    orderId: id,
    billingPartyId: (order as any)?.billing_party_id ?? null,
    tenderId: (order as any)?.tender_id ?? null,
    customer: (order as any)?.billing_party ?? null,
  });

  // Realtime: keep the open order detail page in sync with edits and document
  // changes (manual uploads, replaces, removes, and trailer-driven syncs).
  useEffect(() => {
    if (!id || !tenantId) return;
    const channel = supabase
      .channel(`order-detail-${id}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "orders", filter: `id=eq.${id}` },
        () => {
          queryClient.invalidateQueries({ queryKey: ["order-detail", id, tenantId] });
          queryClient.invalidateQueries({ queryKey: ["order-children", id] });
          queryClient.invalidateQueries({ queryKey: ["trailer-load-status"] });
        },
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "order_bol_images", filter: `order_id=eq.${id}` },
        () => {
          queryClient.invalidateQueries({ queryKey: ["bol-count", id] });
          queryClient.invalidateQueries({ queryKey: ["bol-images", id] });
          queryClient.invalidateQueries({ queryKey: ["bol-images-count", id] });
          queryClient.invalidateQueries({ queryKey: ["bol-extractions", id] });
        },
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "order_tender_images", filter: `order_id=eq.${id}` },
        () => {
          queryClient.invalidateQueries({ queryKey: ["tender-count", id] });
          queryClient.invalidateQueries({ queryKey: ["tender-images", id] });
          queryClient.invalidateQueries({ queryKey: ["tender-images-count", id] });
          queryClient.invalidateQueries({ queryKey: ["tender-extractions", id] });
        },
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [id, tenantId, queryClient]);

  // Tenant locations cache — resolves each stop's IANA timezone for "airline
  // times" wall-clock display (Ready Time at Ship From tz, Delivery at Ship To tz).
  const { data: locations, isFetched: locationsFetched } = useQuery({
    queryKey: ["locations", tenantId],
    queryFn: async () => {
      // `locations` is tenant-scoped indirectly via `customer_id → customers.tenant_id`,
      // not by a direct `tenant_id` column. We fetch the full list and rely on RLS for
      // tenant isolation. The cache key is keyed by `tenantId` so switching environments
      // invalidates this entry without colliding with other pages' `["locations"]` caches.
      const { data } = await supabase.from("locations").select("*").eq("archived", false).order("short_code");
      return (data ?? []) as Location[];
    },
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000,
  });

  // trailerLoadStatus is declared below, after `displayOrder` is computed, so
  // it can key off the leg-effective trailer_id when a leg tab is selected.

  // On-demand EAPI status sync for this order
  useEapiStatusSync(order ? [order] : undefined);

  // Phase 5: a parent is either a legacy multi-leg parent (leg_sequence === 0)
  // or any non-child order that has more than one row in public.legs. We detect
  // the latter by reading leg_count from a parallel query.
  const isChildLeg = !!order?.parent_order_id;

  // Redirect child order URLs to parent with ?leg= param
  useEffect(() => {
    if (isChildLeg && order?.parent_order_id) {
      navigate(`/orders/${order.parent_order_id}?leg=${order.id}`, { replace: true });
    }
  }, [isChildLeg, order?.parent_order_id, order?.id, navigate]);

  // Fetch legs for multi-leg parent orders. Phase 5 reads exclusively from
  // public.legs (canonical source). For legacy multi-leg orders the trigger
  // mirrored child orders into legs, so this also serves them. Each row's
  // `id` is the leg row's UUID and is used as `?leg=` in the URL.
  const {
    data: childLegs,
    isPending: legsPending,
    isFetching: legsFetching,
    isError: legsError,
  } = useOrderLegs(id, { enabled: !isChildLeg });
  // A parent order shows leg tabs only when there are 2+ legs.
  const isParent = (childLegs?.length ?? 0) > 1;
  const legsWithArrivedTrailer = useLegsWithArrivedTrailer(childLegs ?? [], order?.status);

  // Confirmation runs off the stored order + legs — no hidden form.
  const confirm = useConfirmOrder({
    order,
    legs: childLegs,
    legsPending,
    legsError,
    documentBlockers: confirmDocGate.missing,
    documentOverride: docsOverridden,
    isAdmin: !!isAdmin,
    userEmail: user?.email,
    onConfirmed: () => setShowConfirmLegs(false),
  });

  // Per-leg status control. Rules come from the shared helper the driver app
  // reads; the write goes through the shared progression hook in status-only
  // mode (no trailer moves, no yard events, no customer emails).
  const legStatusPlan = planLegStatusActions({
    legs: (childLegs ?? []).map((l: any) => ({
      id: l.id,
      leg_sequence: l.leg_sequence,
      driver_status: l.driver_status,
      eapi_status: l.eapi_status ?? null,
      trip_type: l.trip_type,
    })),
    orderStatus: (order as any)?.status ?? null,
    orderTripType: (order as any)?.trip_type ?? null,
    isAdmin,
  });
  const legStatusProgression = useLegStatusProgression({
    actorLabel: user?.email || "Dispatcher",
    requirePriorInTransit: true,
    // Defaults cover every order-shaped cache (detail, legs, the edit
    // dialog's own leg/trip caches, lists) — see src/lib/order-cache-keys.ts.
  });


  // Initialize selectedLegId from query param or reset on navigation
  useEffect(() => {
    const legParam = searchParams.get("leg");
    setSelectedLegId(legParam || null);
  }, [id, searchParams]);

  // Determine which order to display in the content area. When a leg is
  // selected, merge the parent order's metadata (status, unique_id, billing,
  // trailer, etc.) with the leg's per-leg fields (stops, times, assignments).
  const selectedChildLeg = selectedLegId && childLegs
    ? childLegs.find((l: any) => l.id === selectedLegId) ?? null
    : null;
  const childLegCount = childLegs?.length ?? 0;

  const displayOrder: OrderWithRelations | undefined = selectedChildLeg && order
    ? ({
        ...(order as any),
        // Per-leg overrides
        ship_from_street_address: selectedChildLeg.ship_from_street_address,
        ship_from_city: selectedChildLeg.ship_from_city,
        ship_from_province_state: selectedChildLeg.ship_from_province_state,
        ship_from_postal_zip_code: selectedChildLeg.ship_from_postal_zip_code,
        ship_from_country: selectedChildLeg.ship_from_country,
        ship_from_location_id: selectedChildLeg.ship_from_location_id,
        ship_to_street_address: selectedChildLeg.ship_to_street_address,
        ship_to_city: selectedChildLeg.ship_to_city,
        ship_to_province_state: selectedChildLeg.ship_to_province_state,
        ship_to_postal_zip_code: selectedChildLeg.ship_to_postal_zip_code,
        ship_to_country: selectedChildLeg.ship_to_country,
        ship_to_location_id: selectedChildLeg.ship_to_location_id,
        ready_time: selectedChildLeg.ready_time,
        ready_time_end: selectedChildLeg.ready_time_end,
        requested_delivery_time: selectedChildLeg.requested_delivery_time,
        requested_delivery_time_end: selectedChildLeg.requested_delivery_time_end,
        actual_departure_time: selectedChildLeg.actual_departure_time ?? (order as any).actual_departure_time,
        actual_arrival_time: selectedChildLeg.actual_arrival_time ?? (order as any).actual_arrival_time,
        assigned_tractor_id: selectedChildLeg.assigned_tractor_id,
        assigned_operator_id: selectedChildLeg.assigned_operator_id,
        assigned_operator_2_id: selectedChildLeg.assigned_operator_2_id,
        assigned_tractor: selectedChildLeg.assigned_tractor ?? null,
        assigned_operator: selectedChildLeg.assigned_operator ?? null,
        assigned_operator_2: selectedChildLeg.assigned_operator_2 ?? null,
        // Equipment: the parent order's trailer columns mirror leg 1 only, so
        // a later leg with no trailer of its own must show blank rather than
        // leg 1's trailer.
        trailer_type_id:
          selectedChildLeg.trailer_type_id ??
          (legMirrorsParentEquipment(selectedChildLeg.leg_sequence, childLegCount)
            ? (order as any).trailer_type_id
            : null),
        trailer_id:
          selectedChildLeg.trailer_id ??
          (legMirrorsParentEquipment(selectedChildLeg.leg_sequence, childLegCount)
            ? (order as any).trailer_id
            : null),

        leg_sequence: selectedChildLeg.leg_sequence,
        // Per-leg trip type with fallback to parent so the Order Summary on
        // the leg tab reflects the leg's own trip type when set.
        trip_type: selectedChildLeg.trip_type ?? (order as any).trip_type,
        status: resolveLegStatus(selectedChildLeg, (order as any).status, isParent),
        // Per-leg EAPI trip id (resolved via public.trips active row)
        eapi_trip_id: selectedChildLeg.eapi_trip_id ?? null,
        // Keep the parent's id so deep-linking and queries against the order
        // still resolve to the parent row.
        id: (order as any).id,
        // Expose the leg row id for components that need it.
        _leg_id: selectedChildLeg.id,
      } as OrderWithRelations)
    : order && childLegCount === 1
      ? ({
          ...(order as any),
          // Single-leg: the external trip belongs to leg 1 (legs + trips),
          // never the deprecated `orders.eapi_trip_id` column (AOP-WENP-03315).
          eapi_trip_id: (childLegs as any[])[0].eapi_trip_id ?? null,
          eapi_status: (childLegs as any[])[0].eapi_status ?? null,
          _leg_id: (childLegs as any[])[0].id,
        } as OrderWithRelations)
      : order
        ? // No leg selected (multi-leg overview) or legs still loading: the
          // order itself has no trip; each leg tab shows its own.
          ({ ...(order as any), eapi_trip_id: null } as OrderWithRelations)
        : order;
  const displayOrderId = displayOrder?.id;

  // Effective trailer id: the selected leg's own trailer. The parent order's
  // trailer_id is a leg-1 mirror, so it may only stand in for leg 1 (or a
  // single-leg order) — otherwise leg 2's tab would show leg 1's trailer and
  // its load status.
  const effectiveTrailerId =
    (selectedChildLeg?.trailer_id as string | null | undefined) ??
    (!selectedChildLeg ||
    legMirrorsParentEquipment(selectedChildLeg.leg_sequence, childLegCount)
      ? (order?.trailer_id as string | null | undefined)
      : null) ??
    null;


  const { data: trailerLoadStatus } = useQuery({
    queryKey: ["trailer-load-status", effectiveTrailerId, tenantId],
    enabled: !!effectiveTrailerId && !!tenantId,
    queryFn: async () => {
      const { data } = await supabase
        .from("trailers")
        .select("load_status")
        .eq("trailer_id", effectiveTrailerId!)
        .eq("tenant_id", tenantId!)
        .maybeSingle();
      return data?.load_status ?? null;
    },
  });

  // Re-render every 60s so lateness styling updates without a manual reload.
  // Delivered orders show the trailer's load status as of delivery, not the
  // trailer's live state (which has usually moved on to a later trip).
  const loadStatusAtDelivery = useOrderLoadStatusAtDelivery(
    displayOrder?.status === "delivered" && displayOrder?.id ? [displayOrder.id] : [],
  );

  const [, setNowTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setNowTick((n) => n + 1), 60_000);
    return () => window.clearInterval(id);
  }, []);
  const lateDep = displayOrder ? isLateDeparture(displayOrder as any) : false;
  const lateArr = displayOrder ? isLateArrival(displayOrder as any) : false;
  const warningTextClass = "text-[hsl(var(--aurora-status-warning))]";

  // Reset map state when navigating to a different order
  const prevOrderIdRef = useRef(displayOrderId);
  useEffect(() => {
    if (prevOrderIdRef.current !== displayOrderId) {
      prevOrderIdRef.current = displayOrderId;
      setOriginCoord(null);
      setDestCoord(null);
      setRouteCoords(null);
      setMultiLegCoords([]);
      setMultiLegRoutes([]);
    }
  }, [displayOrderId]);

  const { data: recurringTemplate } = useQuery({
    queryKey: ["recurring-template", displayOrder?.recurring_order_template_id],
    queryFn: async () => {
      const { data } = await supabase
        .from("recurring_order_templates")
        .select("unique_id")
        .eq("id", displayOrder!.recurring_order_template_id!)
        .maybeSingle();
      return data;
    },
    enabled: !!displayOrder?.recurring_order_template_id,
  });

  // EAPI trip verification
  const { eapiClient, isReady: eapiReady } = useEapiAuth();
  const { data: eapiTripVerified } = useQuery({
    queryKey: ["eapi-trip-verify", displayOrder?.eapi_trip_id],
    queryFn: async () => {
      try {
        const res = await eapiClient!.getTrip(displayOrder!.eapi_trip_id!);
        return res?.status >= 400 ? false : true;
      } catch {
        return false;
      }
    },
    enabled: !!displayOrder?.eapi_trip_id && !!eapiClient && eapiReady,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  // BOL/Tender counts for the displayed order
  const { data: bolCount } = useQuery({
    queryKey: ["bol-count", displayOrderId],
    queryFn: async () => {
      const { count } = await supabase
        .from("order_bol_images")
        .select("*", { count: "exact", head: true })
        .eq("order_id", displayOrderId!);
      return count || 0;
    },
    enabled: !!displayOrderId,
  });

  const { data: tenderCount } = useQuery({
    queryKey: ["tender-count", displayOrderId],
    queryFn: async () => {
      const { count } = await supabase
        .from("order_tender_images")
        .select("*", { count: "exact", head: true })
        .eq("order_id", displayOrderId!);
      return count || 0;
    },
    enabled: !!displayOrderId,
  });
  // Resolve origin/destination via the canonical resolver (see
  // src/lib/order-endpoints.ts). Keeping `originLocation` / `destLocation`
  // names so the surrounding map / coordinate code remains unchanged.
  const originEndpoint = useMemo(
    () => resolveOrderEndpoint(displayOrder as any, "from", locations ?? null),
    [displayOrder, locations],
  );
  const destEndpoint = useMemo(
    () => resolveOrderEndpoint(displayOrder as any, "to", locations ?? null),
    [displayOrder, locations],
  );
  const originLocation = originEndpoint.location;
  const destLocation = destEndpoint.location;

  const originLocationFetched = locationsFetched;
  const destLocationFetched = locationsFetched;

  // Resolve map coordinates once both location queries have settled
  const coordRequestRef = useRef(0);
  useEffect(() => {
    if (!displayOrder || !originLocationFetched || !destLocationFetched) return;

    const requestId = ++coordRequestRef.current;

    async function resolveCoords() {
      let origin: [number, number] | null = null;
      let dest: [number, number] | null = null;

      if (originLocation?.origin_latitude != null && originLocation?.origin_longitude != null) {
        origin = [originLocation.origin_latitude, originLocation.origin_longitude];
      } else {
        const originAddr = buildAddress(
          displayOrder.ship_from_street_address,
          displayOrder.ship_from_city,
          displayOrder.ship_from_province_state,
          displayOrder.ship_from_country,
        );
        origin = await geocodeAddress(originAddr);
      }

      if (destLocation?.destination_latitude != null && destLocation?.destination_longitude != null) {
        dest = [destLocation.destination_latitude, destLocation.destination_longitude];
      } else {
        const destAddr = buildAddress(
          displayOrder.ship_to_street_address,
          displayOrder.ship_to_city,
          displayOrder.ship_to_province_state,
          displayOrder.ship_to_country,
        );
        dest = await geocodeAddress(destAddr);
      }

      if (requestId !== coordRequestRef.current) return;

      setOriginCoord(origin);
      setDestCoord(dest);

      if (!origin || !dest) {
        setRouteCoords(null);
        return;
      }

      const route = await fetchRoute(origin, dest);
      if (requestId !== coordRequestRef.current) return;

      // Preserve an already valid routed line if a later duplicate request fails.
      if (route && route.length >= 2) {
        setRouteCoords(route);
      }
    }

    resolveCoords();
  }, [
    displayOrderId,
    originLocationFetched,
    destLocationFetched,
    originLocation?.origin_latitude,
    originLocation?.origin_longitude,
    destLocation?.destination_latitude,
    destLocation?.destination_longitude,
  ]);

  // Geocode all stops for multi-leg parent overview
  const isShowingOverview = isParent && !selectedLegId;
  useEffect(() => {
    if (!isShowingOverview || !childLegs || childLegs.length === 0) return;

    const addresses: string[] = [];
    addresses.push(buildAddress(childLegs[0].ship_from_street_address, childLegs[0].ship_from_city, childLegs[0].ship_from_province_state, childLegs[0].ship_from_country));
    childLegs.forEach((leg) => {
      addresses.push(buildAddress(leg.ship_to_street_address, leg.ship_to_city, leg.ship_to_province_state, leg.ship_to_country));
    });

    Promise.all(addresses.map(geocodeAddress)).then(async (coords) => {
      setMultiLegCoords(coords);
      // Fetch routes for each consecutive pair
      const routes: ([number, number][] | null)[] = [];
      for (let i = 0; i < coords.length - 1; i++) {
        const from = coords[i];
        const to = coords[i + 1];
        if (from && to) {
          routes.push(await fetchRoute(from, to));
        } else {
          routes.push(null);
        }
      }
      setMultiLegRoutes(routes);
    });

    Promise.all(addresses.map(geocodeAddress)).then(setMultiLegCoords);
  }, [isShowingOverview, childLegs]);

  // Cancel order — delegates to the canonical Sync-then-Persist hook
  // (`useOrderCancellation`), which calls `cancel_order_locally` atomically
  // so the cancel-guard trigger bypass GUC and the UPDATE land in the same
  // transaction. Do NOT inline a direct PATCH here — see useOrderDeletion.ts.
  const { cancelOrderAsync, isCanceling } = useOrderCancellation();

  // Manual status promotions (In Transit / Delivered). These used to live in
  // the edit form's status dropdown; they are now standalone actions with
  // their own gating, audit row and delivered fan-out.
  const statusActions = useOrderStatusTransition(
    order
      ? {
          id: (order as any).id,
          unique_id: (order as any).unique_id,
          status: (order as any).status,
          trailer_id: (order as any).trailer_id ?? null,
          parent_order_id: (order as any).parent_order_id ?? null,
          actual_departure_time: (order as any).actual_departure_time ?? null,
          actual_arrival_time: (order as any).actual_arrival_time ?? null,
        }
      : null,
  );

  // ── Regenerate External Trip ────────────────────────────────────────────
  // Software-issue unblock: abandon the current EAPI trip and immediately
  // create a new one with the same settings. Gated behind a confirmation
  // modal so it isn't triggered casually.
  const { environment: eapiEnvironment } = useEapiAuth();
  const [regenConfirmOpen, setRegenConfirmOpen] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  const handleRegenerateExternalTrip = async () => {
    if (!displayOrder?.eapi_trip_id || !eapiClient || !tenantId) return;
    const oldTripId = displayOrder.eapi_trip_id;
    setRegenerating(true);
    try {
      // Resolve the owning leg from the trip itself, not just from which tab
      // happens to be selected. A leg's trip must never be written back onto
      // the parent order row (that's how AOP-HTSH-06021 lost its regenerate).
      const legRowId =
        ((displayOrder as any)._leg_id as string | undefined) ??
        (await findLegIdByActiveTripId(displayOrder.id, oldTripId)) ??
        undefined;
      // Trip ids live only on legs + trips. Refuse before touching upstream
      // if we can't tell which leg owns this trip.
      if (!legRowId) {
        throw new Error("Could not find the stop that owns this external trip. Nothing was changed.");
      }
      // 1. Abandon the existing external trip.
      await eapiClient.abandonTrip(oldTripId, "Unable to Launch");

      // 2. Resolve trailer type info for the create body. For a leg-owned
      // trip the leg row is authoritative — never fall back to the parent's
      // mirrored trailer/trailer-type/trip-type for legs 2+.
      const isLegTrip = !!legRowId && !!selectedChildLeg;
      const legTrailerTypeId = isLegTrip
        ? ((selectedChildLeg as any).trailer_type_id as string | null | undefined)
        : ((displayOrder as any).trailer_type_id as string | null | undefined);
      let trailerTypeInfo: { kind: string; length_ft: number } | null = null;
      if (legTrailerTypeId) {
        const { data: tt } = await supabase
          .from("trailer_types")
          .select("kind, length_ft")
          .eq("id", legTrailerTypeId)
          .maybeSingle();
        trailerTypeInfo = tt ? { kind: tt.kind, length_ft: tt.length_ft } : null;
      }

      // Same eligibility gate the per-leg sync uses: a Manual (or bobtail on
      // non-enabled tenants) leg must not get a replacement external trip.
      const effectiveTripType = isLegTrip
        ? ((selectedChildLeg as any).trip_type as string | null)
        : ((displayOrder as any).trip_type as string | null);
      if (isNoEapiState(trailerTypeInfo?.kind ?? null, effectiveTripType, activeTenant)) {
        throw new Error(
          "This leg does not use external dispatch (Manual/bobtail), so the abandoned trip was not replaced.",
        );
      }

      const customFields = await buildCustomFieldsForOrder(eapiClient, eapiEnvironment, {
        customerShortCode: (displayOrder as any).billing_party?.short_code,
        // main: send Trip Type to the external system. Draft: for a leg trip
        // the leg's own trip type is authoritative, never the parent mirror.
        tripType: effectiveTripType,
        trailerId: isLegTrip
          ? ((selectedChildLeg as any).trailer_id ?? null)
          : (displayOrder as any).trailer_id,
        operator1Email: (displayOrder as any).assigned_operator?.email,
        operator2Email: (displayOrder as any).assigned_operator_2?.email,
      });

      // Multi-leg regenerate uses the same mapper + `<parent>-L<seq>` naming
      // as the per-leg create path, so a regenerated leg trip is byte-for-byte
      // the trip the edit flow would have made. See AOP-DETM-01738.
      const tripBody = isLegTrip
        ? mapLegToCreateTrip(
            selectedChildLeg as any,
            (order as any).unique_id,
            (selectedChildLeg as any).assigned_tractor ?? null,
            trailerTypeInfo,
            locations ?? null,
            customFields,
            childLegs?.length ?? 1,
          )
        : mapOrderToCreateTrip(
            displayOrder as any,
            (displayOrder as any).assigned_tractor ?? null,
            trailerTypeInfo,
            locations ?? null,
            customFields,
          );
      assertTripBodyHasLatLng(tripBody, {
        flow: "regenerate-external-trip",
        uniqueId: (displayOrder as any).unique_id,
      });

      // 3. Create the replacement trip.
      const res = await eapiClient.createTrip(tripBody);
      const newTripId = (res as any)?.data?.id as string | undefined;
      if (!newTripId) {
        throw new Error("EAPI response did not include a trip ID");
      }
      // Upstream idempotency can echo back stale windows on a same-name
      // create; correct them exactly like the per-leg create path does.
      await correctStaleCreateResponse(eapiClient as any, tripBody, res, {
        label: `regenerate ${isLegTrip ? `leg ${(selectedChildLeg as any).leg_sequence}` : "order"}`,
      });

      // Collision guard: upstream may dedup by name and return an id that is
      // already active on another leg of this order. If we persisted it we'd
      // silently shadow that leg's trip (see AOP-DETM-01738 07-18 incident).
      // Best-effort abandon the returned trip only when it's genuinely new;
      // if it's the same id we already had on this leg, skip the abandon.
      if (legRowId) {
        const activeNow = await listActiveLegTripsForOrder(displayOrder.id);
        const conflict = activeNow.find(
          (t) => t.eapi_trip_id === newTripId && t.leg_id !== legRowId,
        );
        if (conflict) {
          console.error(
            "[OrderDetail] regenerate collision: upstream returned trip id already active on another leg",
            { newTripId, thisLeg: legRowId, conflictLeg: conflict.leg_id },
          );
          throw new Error(
            `Upstream returned an existing trip id (${newTripId}) already assigned to another leg on this order. Regenerate aborted to avoid a duplicate. The abandoned trip has been cleared; please retry.`,
          );
        }
      }

      // 4. Persist the new trip id. Every write here is checked — a silent
      // failure used to leave the order pointing at the abandoned trip while
      // still showing a success toast (AOP-HTSH-06021, 2026-09-15).
      if (legRowId) {
        // Per-leg trip: supersede any active trips row for this leg, then insert.
        const active = await listActiveLegTripsForOrder(displayOrder.id);
        const superseded = await Promise.all(
          active
            .filter((t) => t.leg_id === legRowId)
            .map((t) => supersedeLegTrip(t.trip_row_id, "regenerated")),
        );
        const supersedeFailure = superseded.find((r) => !r.ok);
        if (supersedeFailure) {
          throw new Error(
            `New external trip ${newTripId} was created upstream, but retiring the previous trip record failed (${supersedeFailure.error}). Nothing was saved — please report this order with that trip id.`,
          );
        }
        const inserted = await insertActiveLegTrip({
          legId: legRowId,
          tenantId,
          eapiTripId: newTripId,
        });
        if (!inserted.ok) {
          throw new Error(
            `New external trip ${newTripId} was created upstream, but saving it to this leg failed (${inserted.error}). Please report this order with that trip id.`,
          );
        }
      }

      // 5. Verify the save actually stuck before claiming success — on both
      // the per-leg and the order-level path.
      const mirrored = await listActiveLegTripsForOrder(displayOrder.id);
      const persisted = mirrored.some(
        (t) => t.eapi_trip_id === newTripId && (!legRowId || t.leg_id === legRowId),
      );
      if (!persisted) {
        console.error(
          "[OrderDetail] regenerate: trips mirror did not pick up the new trip id",
          { orderId: displayOrder.id, legRowId, newTripId, mirrored },
        );
        throw new Error(
          `New external trip ${newTripId} was created upstream, but this order still shows the previous trip. Please report this order with that trip id instead of retrying.`,
        );
      }

      toast.success(`External trip regenerated: ${newTripId}`);
      queryClient.invalidateQueries({ queryKey: ["order-detail", id, tenantId] });
      queryClient.invalidateQueries({ queryKey: ["order-children-legs", id] });
      queryClient.invalidateQueries({ queryKey: ["eapi-trip-verify"] });
      setRegenConfirmOpen(false);
    } catch (err) {
      console.error("[OrderDetail] regenerate external trip failed", err);
      toast.error(
        err instanceof Error ? err.message : "Failed to regenerate external trip",
      );
    } finally {
      setRegenerating(false);
    }
  };

  const handleCancelOrder = async (
    payload: import("@/components/orders/CancelOrderDialog").CancelConfirmPayload,
  ) => {
    if (!displayOrder?.id) return;
    try {
      await cancelOrderAsync(displayOrder.id, {
        reason: payload.reasonLabel,
        notes: payload.otherText,
        changeEventReason: payload.reason,
        changeEventOther: payload.otherText,
      });
      setShowCancelDialog(false);
    } catch {
      // toast handled inside useOrderCancellation
    }
  };

  if (isLoading) {
    return (
      <AppLayout title="Order Details">
        <div className="space-y-6 animate-fade-in">
          <Skeleton className="h-10 w-64" />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Skeleton className="h-[400px] lg:col-span-2" />
            <Skeleton className="h-[400px]" />
          </div>
        </div>
      </AppLayout>
    );
  }

  if (!order || !displayOrder) {
    return (
      <AppLayout title="Order Not Found">
        <div className="text-center py-16 space-y-4">
          <p className="text-muted-foreground">This order could not be found.</p>
          <Button variant="outline" onClick={() => navigate("/orders")}>
            Back to Orders
          </Button>
        </div>
      </AppLayout>
    );
  }

  // Map data
  const hasMultiLegMap = isShowingOverview && multiLegCoords.length >= 2 && multiLegCoords.some(Boolean);
  const validMultiCoords = multiLegCoords.filter(Boolean) as [number, number][];

  const hasCoords = originCoord && destCoord;
  const mapCenter: [number, number] = hasMultiLegMap && validMultiCoords.length >= 2
    ? [
        validMultiCoords.reduce((s, c) => s + c[0], 0) / validMultiCoords.length,
        validMultiCoords.reduce((s, c) => s + c[1], 0) / validMultiCoords.length,
      ]
    : hasCoords
      ? [(originCoord[0] + destCoord[0]) / 2, (originCoord[1] + destCoord[1]) / 2]
      : [43.65, -79.38];

  const breadcrumbTitle = (
    <div className="flex items-center gap-1.5">
      <Breadcrumb>
        <BreadcrumbList className="text-xl font-semibold">
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link
                to={(typeof window !== "undefined" && sessionStorage.getItem("lastUrl:/orders")) || "/orders"}
                className="text-xl font-semibold"
              >
                Orders
              </Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage className="text-xl font-semibold">{order.unique_id}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
      <StarOrderButton orderId={order.id} size="md" />
    </div>
  );

  // Build polyline segments for multi-leg map
  const multiLegSegments: [number, number][][] = [];
  if (hasMultiLegMap) {
    for (let i = 0; i < multiLegCoords.length - 1; i++) {
      const from = multiLegCoords[i];
      const to = multiLegCoords[i + 1];
      if (from && to) {
        multiLegSegments.push([from, to]);
      }
    }
  }

  // Build header leg tabs for parent orders
  const legHeaderTabs = isParent && childLegs && childLegs.length > 0 ? (
    <div className="flex items-center gap-1 -mb-px">
      <button
        onClick={() => navigate(`/orders/${id}`, { replace: true })}
        className={`inline-flex items-center px-3 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
          selectedLegId === null
            ? "border-primary text-foreground"
            : "border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground/30"
        }`}
      >
        Overview
      </button>
      {childLegs.map((leg: any) => (
        <button
          key={leg.id}
          onClick={() => navigate(`/orders/${id}?leg=${leg.id}`, { replace: true })}
          className={`inline-flex items-center px-3 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
            selectedLegId === leg.id
              ? "border-primary text-foreground"
              : "border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground/30"
          }`}
        >
          Leg {leg.leg_sequence}
        </button>
      ))}
    </div>
  ) : null;

  return (
    <AppLayout
      title=""
      titleExtra={breadcrumbTitle}
      subtitle={legHeaderTabs}
      actions={
        <div className="flex items-center gap-2">
          {!isReadOnly && displayOrder.status === "booked" && (() => {
            // Confirm Order always acts on the parent order, regardless of
            // which leg tab is active. Every rule — per-stop assets, truck
            // eligibility, out-of-service, documentation — lives in the shared
            // confirm plan, so this button, the stops dialog and the write path
            // can never drift apart.
            const plan = confirm.plan;
            const missingDocs = confirmDocGate.missing;
            const docsHardBlock = confirmDocGate.hardBlocked;
            const isConfirming = confirm.isPending;
            // An admin may still click through a documentation block — they get
            // the acknowledgement dialog first. Everything else must be ready.
            // Admins may override policy gates (documentation, truck
            // eligibility, out of service) after an acknowledgement step.
            const canStart =
              plan.canConfirmAny || (!!isAdmin && confirm.overridePlan.canConfirmAny);
            const needsOverride =
              !!isAdmin &&
              confirm.overridePlan.eligibleLegIds.length > plan.eligibleLegIds.length;
            const disabled =
              !canStart || docsHardBlock || confirm.isLoading || isConfirming;

            const start = () => {
              if (disabled) return;
              if (needsOverride || missingDocs.length > 0) {
                setShowDocsOverride(true);
                return;
              }
              if (plan.isMultiLeg) {
                setShowConfirmLegs(true);
                return;
              }
              confirm.confirmAllEligible();
            };

            const button = (
              <Button variant="default" size="sm" disabled={disabled} onClick={start}>
                {isConfirming ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                )}
                {isConfirming ? "Confirming…" : "Confirm Order"}
              </Button>
            );
            if (!plan.blockedReason && missingDocs.length === 0) return button;
            return (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span tabIndex={0}>{button}</span>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs p-3 space-y-2 text-left">
                    <p className="font-semibold">Can't confirm yet</p>
                    {plan.blockedReason && (() => {
                      const reasons = plan.legs
                        .filter((l) => l.state === "blocked")
                        .flatMap((l) => l.reasons)
                        .filter((r, i, arr) => arr.indexOf(r) === i);
                      // Always say something concrete: when no per-stop reason
                      // exists, the plan's own summary is the explanation.
                      const shown = reasons.length > 0 ? reasons : [plan.blockedReason];
                      return (
                        <div className="space-y-1">
                          <ul className="list-disc pl-4 space-y-0.5">
                            {shown.map((r) => (
                              <li key={r}>{r}</li>
                            ))}
                          </ul>
                          {reasons.length > 0 && (
                            <p className="text-xs opacity-80">
                              {isAdmin && confirm.overridePlan.canConfirmAny
                                ? "As an admin you can override this."
                                : "Open Edit to fix this."}
                            </p>
                          )}
                        </div>
                      );
                    })()}

                    {missingDocs.length > 0 && (
                      <div className="space-y-1">
                        <p className="text-xs uppercase tracking-wide opacity-70">
                          Documentation
                        </p>
                        <ul className="list-disc pl-4 space-y-0.5">
                          {missingDocs.map((d) => (
                            <li key={d}>
                              {d === LOAD_STATUS_MISMATCH ? LOAD_STATUS_MISMATCH_MESSAGE : d}
                            </li>
                          ))}
                        </ul>
                        <p className="text-xs opacity-80">
                          {docsHardBlock
                            ? confirmBlockActionHint(missingDocs)
                            : "As an admin you'll be asked to acknowledge before proceeding."}
                        </p>
                      </div>
                    )}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            );
          })()}
          {/* Per-leg status control. Available once the order is past booked
              (a booked order is confirmed first) and while it's still open. */}
          {!isReadOnly &&
            displayOrder.status !== "booked" &&
            displayOrder.status !== "canceled" &&
            displayOrder.status !== "delivered" &&
            legStatusPlan.legs.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                disabled={legStatusProgression.isUpdating}
                onClick={() => setShowLegStatus(true)}
              >
                {legStatusProgression.isUpdating ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Truck className="h-4 w-4 mr-2" />
                )}
                Update Leg Status
              </Button>
            )}
          {!isReadOnly && (
            <Button variant="outline" size="sm" onClick={() => setEditOrder(order ?? null)}>
              <Pencil className="h-4 w-4 mr-2" />
              Edit
            </Button>
          )}
          {!isReadOnly && displayOrder.status !== "canceled" && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="px-2">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {displayOrder?.status === "in_transit" && displayOrder?.assigned_tractor_id && (
                  <DropdownMenuItem
                    onClick={() => setShowRescueDialog(true)}
                  >
                    <LifeBuoy className="h-4 w-4 mr-2" />
                    Assign Rescue
                  </DropdownMenuItem>
                )}
                {/* Manual status promotions. Gating and the write share one
                    plan (`planStatusTransition`) so a visible action can
                    never be rejected by the mutation. */}
                {(() => {
                  const inTransitPlan = statusActions.planFor("in_transit");
                  const deliveredPlan = statusActions.planFor("delivered");
                  return (
                    <>
                      {inTransitPlan.allowed && (
                        <DropdownMenuItem
                          disabled={statusActions.isPending}
                          onClick={() => statusActions.markInTransit()}
                        >
                          <Truck className="h-4 w-4 mr-2" />
                          Mark In Transit
                        </DropdownMenuItem>
                      )}
                      {deliveredPlan.allowed && (
                        <DropdownMenuItem
                          disabled={statusActions.isPending}
                          onClick={() => statusActions.markDelivered()}
                        >
                          <PackageCheck className="h-4 w-4 mr-2" />
                          Mark Delivered
                        </DropdownMenuItem>
                      )}
                    </>
                  );
                })()}
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onClick={() => setShowCancelDialog(true)}
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  Cancel Order
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      }
    >
      <div className="space-y-6 animate-fade-in">
        {/* Main content grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Map + Summary */}
          <div className="lg:col-span-2 space-y-6">
            {/* Summary card (status + map + origin/dest + times) */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">
                    Order Summary
                    {isShowingOverview && childLegs && childLegs.length > 0 && (
                      <span className="text-xs text-muted-foreground font-normal ml-1">
                        ({childLegs.length} legs)
                      </span>
                    )}
                  </CardTitle>
                  {(order as any).status !== "canceled" && (
                    <div className="flex items-center gap-2">
                      <OrderStatusBadge
                        status={
                          selectedChildLeg
                            ? resolveLegStatus(selectedChildLeg, (order as any).status, isParent)
                            : (order as any).status
                        }
                        late={lateDep || lateArr}
                        legs={selectedChildLeg ? undefined : (childLegs as any[] | undefined)}
                      />
                      {isParent && (
                        <Badge variant="secondary" className="text-xs font-medium">
                          Multi-leg
                        </Badge>
                      )}
                      {displayOrder.trip_type && (
                        <Badge variant="secondary" className="text-xs font-medium">
                          {displayOrder.trip_type}
                        </Badge>
                      )}
                      {displayOrder.external_order_id && (
                        <Badge variant="info" className="text-xs font-medium">
                          API Order
                        </Badge>
                      )}
                      {(order as any).pending_bol === true && (
                        <Badge variant="outline" className="text-xs font-medium">
                          Pending BOL
                        </Badge>
                      )}
                      {recurringTemplate && displayOrder.recurring_order_template_id && (
                        <Link to={`/templates/${displayOrder.recurring_order_template_id}`}>
                          <Badge variant="outline" className="gap-1 text-xs font-normal hover:bg-accent cursor-pointer">
                            <Repeat className="h-3 w-3" />
                            {recurringTemplate.unique_id}
                          </Badge>
                        </Link>
                      )}
                    </div>
                  )}
                </div>
                <div className="pt-3 empty:hidden">
                  <TrailerArrivedNotice legs={legsWithArrivedTrailer} />
                </div>
                {(order as any).status === "canceled" && (
                  <div className="pt-3">
                    <CancellationBanner
                      order={order as any}
                      isMultiLeg={isParent}
                      recurringTemplate={recurringTemplate ?? null}
                    />
                  </div>
                )}
              </CardHeader>
              <CardContent>
                <div className="rounded-lg overflow-hidden border h-[320px] mb-4">
                  <MapContainer
                    center={mapCenter}
                    zoom={6}
                    className="h-full w-full"
                    scrollWheelZoom={false}
                    key={displayOrderId}
                  >
                    <ThemedTileLayer />
                    {hasMultiLegMap ? (
                      <>
                        {multiLegCoords.map((coord, idx) =>
                          coord ? (
                            <Marker key={idx} position={coord} icon={makeNumberedIcon(idx + 1)} />
                          ) : null
                        )}
                        {multiLegSegments.map((segment, idx) => {
                          const route = multiLegRoutes[idx];
                          return route ? (
                            <Polyline
                              key={idx}
                              positions={route}
                              pathOptions={{
                                color: `hsl(${210 + idx * 30}, 70%, 50%)`,
                                weight: 4,
                              }}
                            />
                          ) : (
                            <Polyline
                              key={idx}
                              positions={segment}
                              pathOptions={{
                                color: `hsl(${210 + idx * 30}, 70%, 50%)`,
                                weight: 3,
                                dashArray: "8 4",
                              }}
                            />
                          );
                        })}
                        {validMultiCoords.length >= 2 && (
                          <FitBounds coords={validMultiCoords} />
                        )}
                      </>
                    ) : (
                      <>
                        {originCoord && <Marker position={originCoord} icon={originIcon} />}
                        {destCoord && <Marker position={destCoord} icon={destIcon} />}
                        {hasCoords && (
                          <>
                            {routeCoords && routeCoords.length >= 2 && (
                              <Polyline
                                positions={routeCoords}
                                pathOptions={{ color: routeStrokeColor, weight: 5, opacity: 0.95, dashArray: "10 6" }}
                              />
                            )}
                            <FitBounds coords={[originCoord, destCoord]} />
                          </>
                        )}
                      </>
                    )}
                  </MapContainer>
                </div>

                {/* For parent overview: show legs route chain instead of times/locations */}
                {isShowingOverview && childLegs && childLegs.length > 0 ? (
                  <div className="pt-2">
                    <MultiLegRouteChain
                      parentOrder={order}
                      legs={childLegs as unknown as Parameters<typeof MultiLegRouteChain>[0]["legs"]}
                      onSelectLeg={(legId) => navigate(`/orders/${id}?leg=${legId}`, { replace: true })}
                    />
                  </div>
                ) : (
                  <>
                    {/* Locations */}
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="space-y-1">
                        <div className="flex items-center gap-1.5">
                          <ArrowUpRight className="h-4 w-4 text-muted-foreground" />
                          <p className="text-lg font-bold font-mono text-foreground">
                            {originEndpoint.label}
                          </p>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {[displayOrder.ship_from_street_address, displayOrder.ship_from_city, displayOrder.ship_from_province_state, displayOrder.ship_from_postal_zip_code].filter(Boolean).join(", ")}
                        </p>
                      </div>
                      <div className="space-y-1">
                        <div className="flex items-center gap-1.5">
                          <ArrowDownRight className="h-4 w-4 text-muted-foreground" />
                          <p className="text-lg font-bold font-mono text-foreground">
                            {destEndpoint.label}
                          </p>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {[displayOrder.ship_to_street_address, displayOrder.ship_to_city, displayOrder.ship_to_province_state, displayOrder.ship_to_postal_zip_code].filter(Boolean).join(", ")}
                        </p>
                      </div>
                    </div>

                    {/* Times */}
                    <div className="border-t pt-4 grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Ready on</p>
                        <ReadyTimeDisplay order={displayOrder as any} variant="card" cardSize="base" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Delivering on</p>
                        <DeliveryTimeDisplay order={displayOrder as any} variant="card" cardSize="base" />
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Details (broken into Shipment / Equipment / Assignments / Custom fields) */}
            <OrderDetailsSections
              order={displayOrder}
              trailerTypes={trailerTypes}
              trailerLoadStatus={trailerLoadStatus}
              loadStatusHistorical={
                displayOrder ? loadStatusAtDelivery.get(displayOrder.id) ?? null : null
              }
              hideLegSpecific={isShowingOverview && !!childLegs && childLegs.length > 0}
              eapiTripVerified={eapiTripVerified}
              recurringTemplate={recurringTemplate}
              onRegenerateExternalTrip={
                displayOrder?.eapi_trip_id && !isReadOnly && eapiReady
                  ? () => setRegenConfirmOpen(true)
                  : undefined
              }
              regeneratingExternalTrip={regenerating}
              legTrailers={
                isShowingOverview && childLegs && childLegs.length > 0
                  ? childLegs.map((l: any) => ({
                      leg_sequence: l.leg_sequence,
                      trailer_id: l.trailer_id ?? null,
                      trailer_type_id: l.trailer_type_id ?? null,
                    }))
                  : undefined
              }
            />
          </div>

          {/* Right sidebar */}
          <div className="space-y-6">

            {/* Documents */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Documents
                  {((bolCount || 0) + (tenderCount || 0) > 0) && (
                    <span className="ml-1 text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded-full">
                      {(bolCount || 0) + (tenderCount || 0)}
                    </span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="border rounded-lg p-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-medium">Bill of Lading</h4>
                    <BolViewerDialog orderId={displayOrder.id} orderUniqueId={displayOrder.unique_id} />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {bolCount ? `${bolCount} document${bolCount > 1 ? "s" : ""} uploaded` : "No documents uploaded"}
                  </p>
                </div>
                <div className="border rounded-lg p-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-medium">Tender</h4>
                    <TenderViewerDialog orderId={displayOrder.id} orderUniqueId={displayOrder.unique_id} />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {tenderCount ? `${tenderCount} document${tenderCount > 1 ? "s" : ""} uploaded` : "No documents uploaded"}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Notes */}
            <OrderNotesCard orderId={displayOrder.id} compact />

            {/* History */}
            <OrderHistory order={displayOrder} />
          </div>
        </div>
      </div>

      <OrderEditDialog
        order={editOrder}
        open={!!editOrder}
        onOpenChange={(open) => { if (!open) setEditOrder(null); }}
      />

      <ConfirmDocsOverrideDialog
        open={showDocsOverride}
        onOpenChange={setShowDocsOverride}
        message={confirmDocGate.message}
        reasons={confirm.overriddenReasons}
        onConfirm={() => {
          setShowDocsOverride(false);
          setDocsOverridden(true);
          if (confirm.overridePlan.isMultiLeg) setShowConfirmLegs(true);
          else confirm.confirmLegs(confirm.overridePlan.eligibleLegIds);
        }}
      />

      <ConfirmLegsDialog
        open={showConfirmLegs}
        onOpenChange={setShowConfirmLegs}
        plan={docsOverridden ? confirm.overridePlan : confirm.plan}
        isPending={confirm.isPending}
        pendingLegIds={confirm.pendingLegIds}
        onConfirmLegs={(legIds) => confirm.confirmLegs(legIds)}
      />

      <LegStatusDialog
        open={showLegStatus}
        onOpenChange={setShowLegStatus}
        plan={legStatusPlan}
        isPending={legStatusProgression.isUpdating}
        pendingLegId={legStatusProgression.pendingLegId}
        orderUniqueId={displayOrder?.unique_id}
        onMove={(legId, move) => {
          if (!id || move === "canceled") return;
          void legStatusProgression.progress({
            parentOrderId: id,
            legId,
            newStatus: move,
          });
        }}
        onCancelLeg={(legId, payload) => {
          if (!id) return;
          void legStatusProgression.cancel({
            parentOrderId: id,
            legId,
            reason: payload.reasonLabel,
            notes: payload.otherText,
          });
        }}
      />


      {displayOrder && (
        <CancelOrderDialog
          open={showCancelDialog}
          onOpenChange={setShowCancelDialog}
          orderId={displayOrder.unique_id}
          onConfirm={(payload) => { void handleCancelOrder(payload); }}
          isPending={isCanceling}
        />
      )}

      {displayOrder && (
        <AssignRescueDialog
          open={showRescueDialog}
          onOpenChange={setShowRescueDialog}
          orderId={displayOrder.id}
          orderUniqueId={displayOrder.unique_id}
          tractorName={displayOrder.assigned_tractor?.name}
          currentRescueOperatorId={(displayOrder as any).rescue_operator_id}
        />
      )}

      <AlertDialog open={regenConfirmOpen} onOpenChange={(v) => !regenerating && setRegenConfirmOpen(v)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Regenerate External Trip?</AlertDialogTitle>
            <AlertDialogDescription>
              This will abandon and create a new external trip, this should only be used to unblock software issues. Proceed?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={regenerating}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={regenerating}
              onClick={(e) => {
                e.preventDefault();
                void handleRegenerateExternalTrip();
              }}
            >
              {regenerating ? "Regenerating…" : "Proceed"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}
